import React, { useEffect, useState } from "react";
import { Grid } from "semantic-ui-react";
import { convertStateToAbbreviation, convertToDateString, deepCopy, filterOutCollateral, getDropdownOptions, guid, handleGetValueFromId, handleInputType } from "../../../Helpers";
import { getIsWhiteLabeled } from "../../../helpers/PermissionHelper";
import { BrokerConstants } from "../../constants/BrokerConstants";
import { GlobalConstants } from "../../constants/GlobalConstants";
import { FeatureFlags } from "../../constants/LocalServerConstants";
import BCFormInput from "../../ui/BCFormInput";
import BCTable from "../../ui/BCTable";
import BCValidationMessage from "../../ui/BCValidationMessage";
import { BPConstructionScheduleTable } from "../BPApplicationTables";
import BPLoanDetailSection from "./BPLoanDetailSection";

interface ILoanProjectContentProps {
  applicationMetadata: any;
  brokerColor: any;
  isEditMode: boolean;
  accordionProps: any;
  isLoanInfo: any;
  projectInfo: any;
  projectPatchObj: any;
  setProjectPatchObj: any;
  propertiesPatchArray: any;
  setPropertiesPatchArray: any;
  propertiesCopy: any;
  setActiveTab: React.Dispatch<React.SetStateAction<string>>;
  borrowerApplicationInfo: any;
  handleDeleteCostsPaidsAsync: any;
  handleUnfocus?: any;
  invalidProps: any;
}

interface ITermScheduleUnit {
  id: string;
  monthOfFirstDraw?: any;
  buildTimeInMonths?: any;
  timeFrameToSellOrRefinanceInMonths?: any;
}

const BPLoanProjectContent = ({ ...props }: ILoanProjectContentProps) => {
  const [builderIsBorrwer, setBuilderIsBorrower] = useState(false as boolean);
  const [costsPaidPatchArray, setCostsPaidPatchArray] = useState([] as ITermScheduleUnit[]);
  const [termSchedulePatchArray, setTermSchedulePatchArray] = useState([] as ITermScheduleUnit[]);
  const [financialsPatchObj, setFinancialsPatchObj] = useState({} as any);
  const [siteDevelopmentPatchObj, setSiteDevelopmentPatchObj] = useState({} as any);
  const [builderInfoPatchObj, setBuilderInfoPatchObj] = useState({} as any);
  const [checkedPropertySchedules, setCheckedPropertySchedules] = useState([] as string[]);

  const editBuilderInBorrwerTab = () => {
    props.setActiveTab(BrokerConstants.InteriorCardTabs.Borrower.name.toLocaleUpperCase());
    props.handleUnfocus();
  };

  const handleBuilderInfoChange = () => {
    if (!builderInfoPatchObj.id && props.projectInfo.builder && props.projectInfo.builder.builderOrganizationId) {
      builderInfoPatchObj["id"] = props.projectInfo.builder.builderOrganizationId;
    }
    
    setBuilderInfoPatchObj({...builderInfoPatchObj});
  };

  const handleBuilderAddressChange = (subObj, mainObj, setMainObj) => {
    mainObj.builder = {...mainObj.builder, ...subObj};
    setMainObj({...mainObj});
  };
  
  const handleCalculateAnticipatedPayoff = (property, termSchedulePatchArray) => {
    const propertyUnit = property.loanPropertyUnitReconveyances[0];
    const updatedUnit = termSchedulePatchArray.find(x => x.id === propertyUnit.id);
    const firstDrawTime = (updatedUnit && updatedUnit.monthOfFirstDraw) ? updatedUnit.monthOfFirstDraw : (propertyUnit && propertyUnit.monthOfFirstDraw) ?  propertyUnit.monthOfFirstDraw : 0;
    const buildTime = (updatedUnit && updatedUnit.buildTimeInMonths) ? updatedUnit.buildTimeInMonths : (propertyUnit && propertyUnit.buildTimeInMonths) ?  propertyUnit.buildTimeInMonths : 0;
    const sellOrRefinanceTime = (updatedUnit && updatedUnit.timeFrameToSellOrRefinanceInMonths) ? updatedUnit.timeFrameToSellOrRefinanceInMonths : (propertyUnit && propertyUnit.timeFrameToSellOrRefinanceInMonths) ?  propertyUnit.timeFrameToSellOrRefinanceInMonths : 0;
    if (props.projectInfo.requestedClosingDate && !isNaN(firstDrawTime) && !isNaN(buildTime) && !isNaN(sellOrRefinanceTime)) {
      const closingDate = new Date(props.projectInfo.requestedClosingDate);
      const estimatedMaturityDate = new Date(closingDate.setMonth(closingDate.getMonth() + parseInt(firstDrawTime) + parseInt(buildTime) + parseInt(sellOrRefinanceTime) - 1)).toLocaleDateString();
      return convertToDateString(estimatedMaturityDate, "full");
    }
    else {
      return null;
    }
  };

  const handleDisplayValue = (field, oldObject, PatchArray) => {
    const newObject = PatchArray.find(x => x.id === oldObject.id);
    if (newObject && Object.keys(newObject).includes(field) && props.isEditMode === true) {
      return newObject[field];
    }

    return oldObject[field] ? oldObject[field] : "";
  };

  const handleTermScheudleChanges = (value, field, property) => {
    if (property.loanPropertyUnitReconveyances) {
      for (const unit of property.loanPropertyUnitReconveyances) {
        const selectedUnit = termSchedulePatchArray.find(x => x.id === unit.id);
        if (selectedUnit) {
          selectedUnit[field] = value;
        }
        else {
          const updatedUnit = {
            id: unit.id,
          };

          updatedUnit[field] = value;
          termSchedulePatchArray.push({...updatedUnit});
        }

        setTermSchedulePatchArray([...termSchedulePatchArray]);
        props.projectPatchObj["termSchedule"] = [...termSchedulePatchArray];
      }
    }
  };

  const handlePropertyLoanTermChange = (value, field, property, type) => {
    const formattedValue = handleInputType(type, value, undefined, undefined, 50);
    property[field] = formattedValue;
    const selectedProperty = props.propertiesPatchArray.find(p => p.id === property.id);

    if (selectedProperty) {
      selectedProperty[field] = formattedValue;
    }
    else {
      const updatedProperty = {
        id: property.id,
      };

      updatedProperty[field] = formattedValue;
      props.propertiesPatchArray.push({...updatedProperty});
    }

    props.setPropertiesPatchArray([...props.propertiesPatchArray]);
  };

  const handlePatchArrayChanges = (value, field, objId, patchArray, setPatchArray, patchArrayName) => {
    const selectedObj = patchArray.find(x => x.id === objId);
    if (selectedObj) {
      selectedObj[field] = value;
    }
    else {
      const updatedObj = {
        id: objId,
      };

      updatedObj[field] = value;
      patchArray.push({...updatedObj});
    }

    setPatchArray([...patchArray]);
    props.projectPatchObj[patchArrayName] = [...patchArray];
  };

  const handleAddCostsPaid = () => {
    const costsPaidList = props.projectInfo.loanCostPaids ? props.projectInfo.loanCostPaids : [];
    const newCostPaid = {
      id: guid(),
      loanId: props.projectInfo.id,
      gmrprojectCostId: null,
      amount: null,
      description: null
    };

    costsPaidList.push({...newCostPaid});
    costsPaidPatchArray.push({...newCostPaid});
    props.setProjectPatchObj({...props.projectPatchObj, "totalCostsPaidToDate": [...costsPaidPatchArray]});
  };

  const handleDeleteCostsPaid = async (costsPaidId) => {
    props.projectInfo.loanCostPaids = props.projectInfo.loanCostPaids.filter(x => x.id !== costsPaidId);
    await props.handleDeleteCostsPaidsAsync(costsPaidId);
  };

  const handleTermScheduleDisplayValue = (property, field) => {
    const propertyUnit = property.loanPropertyUnitReconveyances[0];
    const selectedUnit = [...termSchedulePatchArray].find(x => x.id === propertyUnit.id);
    if (selectedUnit && Object.keys(selectedUnit).includes(field) && props.isEditMode) {
      return selectedUnit[field];
    }

    return propertyUnit[field];
  };
  
  const formatLoanUnitsTermSchedule = (loanProperties) => {
    const formattedUnitLoanTerms = [] as any[];
    const filteredOutCollateral = filterOutCollateral(loanProperties);
    filteredOutCollateral.forEach((loanProperty) => {
      const PropertyName = `${loanProperty.street}, ${loanProperty.cityName} ${convertStateToAbbreviation(loanProperty.stateName)} ${loanProperty.zip}`;
        if (formattedUnitLoanTerms.map(obj => obj.Property).indexOf(loanProperty.street) === -1) {
          formattedUnitLoanTerms.push({
            "Property": PropertyName,
            "Month of First Draw": <React.Fragment>
              <BCFormInput
                type="input"
                placeholder="1"
                value={handleTermScheduleDisplayValue(loanProperty, "monthOfFirstDraw")}
                field="monthOfFirstDraw"
                unitType="Months"
                handleFormChange={(value, field) => handleTermScheudleChanges(value, field, loanProperty)}
                disabled={!props.isEditMode}
              />
              <BCValidationMessage invalidProps={props.invalidProps} prop={`termSchedule-${loanProperty.loanPropertyUnitReconveyances[0].id}-monthOfFirstDraw`} />
            </React.Fragment>,
            "Months to Finish Construction": <React.Fragment>
              <BCFormInput
                type="input"
                placeholder="1"
                value={handleTermScheduleDisplayValue(loanProperty, "buildTimeInMonths")}
                field="buildTimeInMonths"
                unitType="Months"
                handleFormChange={(value, field) => handleTermScheudleChanges(value, field, loanProperty)}
                disabled={!props.isEditMode}
              />
              <BCValidationMessage invalidProps={props.invalidProps} prop={`termSchedule-${loanProperty.loanPropertyUnitReconveyances[0].id}-buildTimeInMonths`} />
            </React.Fragment>,
            "Months to Sell or Refinance": <React.Fragment>
              <BCFormInput
                type="input"
                placeholder="1"
                value={handleTermScheduleDisplayValue(loanProperty, "timeFrameToSellOrRefinanceInMonths")}
                field="timeFrameToSellOrRefinanceInMonths"
                unitType="Months"
                handleFormChange={(value, field) => handleTermScheudleChanges(value, field, loanProperty)}
                disabled={!props.isEditMode}
              />
              <BCValidationMessage invalidProps={props.invalidProps} prop={`termSchedule-${loanProperty.loanPropertyUnitReconveyances[0].id}-timeFrameToSellOrRefinanceInMonths`} />
            </React.Fragment>,
            "Anticipated Payoff": handleCalculateAnticipatedPayoff(loanProperty, termSchedulePatchArray)
          });
      }
    });

    return formattedUnitLoanTerms;
  };

  const formatApplicationGMRCostsPaid = (loanCostPaids) => {
    const formattedCostsPaid = [] as any[];
    loanCostPaids.forEach((costPaid) => {
      formattedCostsPaid.push({
        "id": costPaid.id,
        "Cost": <React.Fragment>
          <BCFormInput
            type="dropdown"
            labelClass="gmr-background-label"
            dropdownOptions={getDropdownOptions(props.applicationMetadata.gmrprojectCosts, ["id"], "name")}
            value={handleDisplayValue("gmrprojectCostId", costPaid, costsPaidPatchArray)}
            field="gmrprojectCostId"
            handleFormChange={(value, field) => handlePatchArrayChanges(value, field, costPaid.id, costsPaidPatchArray, setCostsPaidPatchArray, "totalCostsPaidToDate")}
            disabled={!props.isEditMode}
          />
          <BCValidationMessage invalidProps={props.invalidProps} prop={`costsPaid-${costPaid.id}-gmrprojectCostId`} />
        </React.Fragment>,
        "Amount": <React.Fragment>
          <BCFormInput
            labelClass="gmr-background-label"
            type="input"
            placeholder="10000"
            fieldType="dollar"
            inputType="number"
            value={handleDisplayValue("amount", costPaid, costsPaidPatchArray)}
            field="amount"
            handleFormChange={(value, field) => handlePatchArrayChanges(value, field, costPaid.id, costsPaidPatchArray, setCostsPaidPatchArray, "totalCostsPaidToDate")}
            disabled={!props.isEditMode}
          />
          <BCValidationMessage invalidProps={props.invalidProps} prop={`costsPaid-${costPaid.id}-amount`} />
        </React.Fragment>,
        "Description": <BCFormInput
          labelClass="gmr-background-label"
          type="input"
          inputType="text"
          value={handleDisplayValue("description", costPaid, costsPaidPatchArray)}
          field="description"
          handleFormChange={(value, field) => handlePatchArrayChanges(value, field, costPaid.id, costsPaidPatchArray, setCostsPaidPatchArray, "totalCostsPaidToDate")}
          disabled={!props.isEditMode}
        />
      });
    });

    return formattedCostsPaid;
  };

  useEffect(() => {
    const builderIdentity = props.borrowerApplicationInfo.whoWillDoConstructionBorrowerApplicationInputId;
    if (builderIdentity && builderIdentity === BrokerConstants.Builder.BuilderIsBorrower) {
      setBuilderIsBorrower(true);
    }
    else {
      setBuilderIsBorrower(false);
    }
  }, []);

  return (
    <React.Fragment>
      <BPLoanDetailSection
        title={BrokerConstants.InteriorCardAccordions.DetailsOverview.name}
        accordionId={BrokerConstants.InteriorCardAccordions.DetailsOverview.id}
        mainPatchObj={props.projectPatchObj}
        setMainPatchObj={props.setProjectPatchObj}
        subPatchObj={props.projectPatchObj}
        setSubPatchObj={props.setProjectPatchObj}
        isEditMode={props.isEditMode}
        {...props.accordionProps}
        invalidProps={props.invalidProps}
        inputs={[
          {
            value: props.projectInfo.name,
            label: "Project Name:",
            size: 4,
            field: "name",
            readonly: true
          },
          {
            value: props.projectInfo.loanProgramId,
            label: "Loan Program:",
            size: 4,
            field: "loanProgramId",
            dropdown: getDropdownOptions(props.applicationMetadata.loanPrograms, ["id"], "name"),
            readonly: true
          },
          {
            value: props.projectInfo,
            original: props.borrowerApplicationInfo,
            address: true,
            defaultValueObj: {...props.projectInfo},
            placeDetailsObject: props.projectPatchObj,
            setPlaceDetailsObject: props.setProjectPatchObj,
            fieldsNameOverridesArray: [
              {
                addressComponent: "address",
                field: "street"
              },
              {
                addressComponent: "city",
                field: "cityName"
              },
              {
                addressComponent: "state",
                field: "stateName"
              },
              {
                addressComponent: "county",
                field: "countyName"
              },
              {
                addressComponent: "zip",
                field: "zip"
              },
            ],
            label: "Address",
            size: 8,
            required: true
          },
          {
            calendar: true,
            value: props.projectInfo.requestedClosingDate,
            label: "Desired closing date:",
            size: 4,
            field: "requestedClosingDate",
            original: props.borrowerApplicationInfo.requestedClosingDate,
            required: true
          },
          {
            value: props.projectInfo.requestedLoanTermInMonths,
            label: "Desired Loan Term:",
            size: 4,
            unitType: "Months",
            inputType: GlobalConstants.InputTypes.INT,
            field: "requestedLoanTermInMonths",
            original: props.borrowerApplicationInfo.requestedLoanTermInMonths,
            required: true
          },
          {
            value: props.projectInfo.budget,
            label: "Construction Budget:",
            size: 4,
            unitType: "$",
            field: "budget",
            original: props.borrowerApplicationInfo.budget,
            required: true
          }
        ]}
        validationGroupName="project"
      />
      <BPLoanDetailSection
        title={BrokerConstants.InteriorCardAccordions.DetailsFinancials.name}
        accordionId={BrokerConstants.InteriorCardAccordions.DetailsFinancials.id}
        mainPatchObj={props.projectPatchObj}
        setMainPatchObj={props.setProjectPatchObj}
        subPatchObjName="financial"
        subPatchObj={financialsPatchObj}
        setSubPatchObj={setFinancialsPatchObj}
        isEditMode={props.isEditMode}
        {...props.accordionProps}
        inputs={[
          ...(FeatureFlags.IS_UNIT_PROPERTY_REDESIGN ? [{
            value: props.borrowerApplicationInfo.purchaseRefinanceOptionLookupId,
            label: "Ownership type:",
            size: 4,
            field: "purchaseRefinanceOptionLookupId",
            dropdown: getDropdownOptions(props.applicationMetadata.purchaseRefinance, ["id"], "name"),
            original: props.borrowerApplicationInfo.purchaseRefinanceOptionLookupId,
            originalIdValueFrom: props.applicationMetadata.purchaseRefinance,
            readonly: true
          },
          {
            value: props.projectInfo.howLongOwnedBorrowerApplicationInputId,
            label: "Length of ownership:",
            size: 4,
            field: "howLongOwnedBorrowerApplicationInputId",
            dropdown: getDropdownOptions(props.applicationMetadata.ownershipInputs, ["id"], "name"),
            original: props.borrowerApplicationInfo.howLongOwnedBorrowerApplicationInputId,
            originalIdValueFrom: props.applicationMetadata.ownershipInputs,
            condition: props.borrowerApplicationInfo.purchaseRefinanceOptionLookupId !== GlobalConstants.PurchaseOrRefinanceOptionIds.PURCHASE
          },
          {
            value: props.projectInfo.originalPurchasePrice,
            label: "Purchase price:",
            size: 4,
            unitType: "$",
            field: "originalPurchasePrice",
            original: props.borrowerApplicationInfo.originalPurchasePrice
          },
          {
            value: props.projectInfo.estimatePropertyValue,
            label: "Estimated value:",
            size: 4,
            unitType: "$",
            field: "estimatePropertyValue",
            original: props.borrowerApplicationInfo.estimatePropertyValue,
            condition: props.borrowerApplicationInfo.purchaseRefinanceOptionLookupId !== GlobalConstants.PurchaseOrRefinanceOptionIds.PURCHASE
          },
          {
            value: props.projectInfo.amountOwed,
            label: "Amount currently owed:",
            size: 4,
            unitType: "$",
            field: "amountOwed",
            original: props.borrowerApplicationInfo.amountOwed,
            condition: [GlobalConstants.PurchaseOrRefinanceOptionIds.REFINANCE_EXTERNAL, GlobalConstants.PurchaseOrRefinanceOptionIds.REFINANCE_INTERNAL].indexOf(props.borrowerApplicationInfo.purchaseRefinanceOptionLookupId) > -1
          },
          {
            value: props.projectInfo.cashOutAmountRequested,
            label: "Expected cash back at closing:",
            size: 4,
            unitType: "$",
            field: "cashOutAmountRequested",
            original: props.borrowerApplicationInfo.cashOutAmountRequested,
            condition: props.borrowerApplicationInfo.purchaseRefinanceOptionLookupId === GlobalConstants.PurchaseOrRefinanceOptionIds.OWN
          },
          {
            value: props.projectInfo.subordinatedDebtAmount,
            label: "Seller carryback/Subordinated debt:",
            size: 4,
            unitType: "$",
            field: "subordinatedDebtAmount",
            original: props.borrowerApplicationInfo.subordinateDebtAmount
          },
          {
            value: props.projectInfo.payoffStrategyId,
            label: "Payoff strategy:",
            size: 4,
            field: "payoffStrategyId",
            dropdown: getDropdownOptions(props.applicationMetadata.payoffStrategies, ["id"], "name"),
            original: props.borrowerApplicationInfo.payoffStrategyId,
            originalIdValueFrom: props.applicationMetadata.payoffStrategies
          },
          {
            value: props.projectInfo.permitStatusId,
            label: "Permit status:",
            size: 4,
            field: "permitStatusId",
            dropdown: getDropdownOptions(props.applicationMetadata.permitStatuses, ["id"], "name"),
            original: props.borrowerApplicationInfo.permitStatusId,
            originalIdValueFrom: props.applicationMetadata.permitStatuses
          },
          ...(FeatureFlags.IS_RETAIL_PHASE_2 ? [{  
            value: props.borrowerApplicationInfo.developmentOrVerticalPermitTimingInMonths,
            label: "Months till permit issued:",
            size: 4,
            field: "developmentOrVerticalPermitTimingInMonths"
          },
          {
            value: props.borrowerApplicationInfo.permitStatusExplanation,
            label: "Permit additional details:",
            size: 4,
            field: "permitStatusExplanation",
            textarea: "textarea"
          }] :[]),
          ] : [{
            value: handleGetValueFromId(props.applicationMetadata.ownershipInputs, props.projectInfo.howLongOwnedBorrowerApplicationInputId),
            label: "How long have you owned the property?",
            size: 4,
            field: "howLongOwnedBorrowerApplicationInputId",
            readonly: true
          },
          {
            value: props.projectInfo.originalPurchasePrice,
            label: "Original purchase price:",
            size: 4,
            unitType: "$",
            field: "originalPurchasePrice",
            original: props.borrowerApplicationInfo.originalPurchasePrice
          },
          {
            value: props.projectInfo.amountOwed,
            label: "Amount currently owed:",
            size: 4,
            unitType: "$",
            field: "amountOwed",
            original: props.borrowerApplicationInfo.amountOwed
          },
          {
            value: props.projectInfo.subordinatedDebtAmount,
            label: "Seller Carryback/Subordinated debt:",
            size: 4,
            unitType: "$",
            field: "subordinatedDebtAmount",
            original: props.borrowerApplicationInfo.subordinateDebtAmount
          },
          {
            value: props.projectInfo.payoffStrategyId,
            label: "Payoff Strategy:",
            size: 4,
            field: "payoffStrategyId",
            dropdown: getDropdownOptions(props.applicationMetadata.payoffStrategies, ["id"], "name"),
            original: props.borrowerApplicationInfo.payoffStrategyId,
            originalIdValueFrom: props.applicationMetadata.payoffStrategies
          },
          {
            value: props.projectInfo.permitStatusId,
            label: "Permit Status:",
            size: 4,
            field: "permitStatusId",
            dropdown: getDropdownOptions(props.applicationMetadata.permitStatuses, ["id"], "name"),
            original: props.borrowerApplicationInfo.permitStatusId,
            originalIdValueFrom: props.applicationMetadata.permitStatuses
          }])
        ]}
        table={props.projectInfo.loanCostPaids && props.projectInfo.loanCostPaids.length > 0 && <Grid>
          <Grid.Row className="details-row">
            <Grid.Column width={16}>
              <BCFormInput
                labelClass="gmr-background-label"
                label="Costs Paid to Date"
              />
            </Grid.Column>
            <Grid.Column width={16}>
              <BCTable
                dataRules={[{ field: "id", rule: "hide" }]}
                data={formatApplicationGMRCostsPaid(props.projectInfo.loanCostPaids)}
                editMode={props.isEditMode && props.isLoanInfo}
                actions={["add", "delete"]}
                isDeleteHide={!props.isEditMode}
                handleTableAdd={() => handleAddCostsPaid()}
                handleTableDeleteById={handleDeleteCostsPaid}
                thin
                dataSingleLine
                overflowX
                unstackable
                brokerColor={getIsWhiteLabeled() ? props.brokerColor : void 0}
              />
            </Grid.Column>
          </Grid.Row>
        </Grid>}
      />
      <BPLoanDetailSection
        title={BrokerConstants.InteriorCardAccordions.DetailsSiteDevelopment.name}
        accordionId={BrokerConstants.InteriorCardAccordions.DetailsSiteDevelopment.id}
        mainPatchObj={props.projectPatchObj}
        setMainPatchObj={props.setProjectPatchObj}
        subPatchObjName="siteDevelopment"
        subPatchObj={siteDevelopmentPatchObj}
        setSubPatchObj={setSiteDevelopmentPatchObj}
        isEditMode={props.isEditMode}
        {...props.accordionProps}
        inputs={[
          {
            value: props.projectInfo.sizeOfCurrentProperty,
            label: `Size of current ${FeatureFlags.IS_BETA ? "plat" : "property"}`,
            size: 4,
            unitType: "Acres",
            inputType: GlobalConstants.InputTypes.INT,
            field: "sizeOfCurrentProperty",
            original: props.borrowerApplicationInfo.sizeOfCurrentProperty
          },
          {
            value: props.projectInfo.noOfProposedLots,
            label: "Lots to be developed:",
            size: 4,
            unitType: "Lots",
            inputType: GlobalConstants.InputTypes.INT,
            field: "noOfProposedLots",
            original: props.borrowerApplicationInfo.noOfProposedLots
          },
          {
            value: props.projectInfo.sizeOfProposedLot,
            label: `${FeatureFlags.IS_BETA ? "Average s" : "S"}ize of proposed lots:`,
            size: 4,
            unitType: "Square Feet",
            inputType: GlobalConstants.InputTypes.INT,
            field: "sizeOfProposedLot",
            readonly: true,
            original: props.borrowerApplicationInfo.sizeOfProposedLot,
          },
          ...(FeatureFlags.IS_BETA && (props.projectInfo.loanProgramId === GlobalConstants.LoanProgramIds.ALL_IN_ONE || props.projectInfo.loanProgramId === GlobalConstants.LoanProgramIds.DEVELOPMENT) ? [
            {
              value: props.projectInfo.afterRepairValue,
              label: "Expected combined finished lot value (i.e. ARV):",
              size: 4,
              unitType: "$",
              field: "afterRepairValue",
              original: props.borrowerApplicationInfo.afterRepairValue
            },
          ] : []),          
          {
            value: props.projectInfo.developmentBeginsAfterClosingInMonths,
            label: "Months after closing development begins:",
            size: 4,
            unitType: "Months",
            inputType: GlobalConstants.InputTypes.INT,
            field: "developmentBeginsAfterClosingInMonths",
            original: props.borrowerApplicationInfo.developmentBeginsAfterClosingInMonths
          },
          {
            value: props.projectInfo.timeFrameToDevelopInMonths,
            label: "Development length:",
            size: 4,
            unitType: "Months",
            inputType: GlobalConstants.InputTypes.INT,
            field: "timeFrameToDevelopInMonths",
            original: props.borrowerApplicationInfo.timeFrameToDevelopInMonths
          },
          {
            value: props.projectInfo.horizontalDevelopmentBudget,
            label: "Horizontal Development Budget",
            size: 4,
            unitType: "$",
            field: "horizontalDevelopmentBudget",
            original: props.borrowerApplicationInfo.horizontalDevelopmentBudget
          },
          {
            value: props.projectInfo.cashOutAmountRequested,
            label: "Cash out at Closing:",
            size: 4,
            unitType: "$",
            field: "cashOutAmountRequested",
            original: props.borrowerApplicationInfo.cashOutAmountRequested
          },
          ...(FeatureFlags.IS_RETAIL_PHASE_2 && (props.projectInfo === GlobalConstants.LoanProgramIds.ALL_IN_ONE || props.projectInfo === GlobalConstants.LoanProgramIds.DEVELOPMENT || props.projectInfo.loanProgramId === GlobalConstants.LoanProgramIds.BRIDGE) ? [
          {
            inputSuffix: true,
            value: props.borrowerApplicationInfo.sizeOfCurrentProperty,
            label: "Size of current property:",
            size: 4,
            inputType: GlobalConstants.InputTypes.INT,
            fieldType: "inputSuffix",
            field: "sizeOfCurrentProperty",
            unitField: "sizeOfCurrentPropertyUnitOfMeasurementBorrowerApplicationInputId",
            dropdownOptions: getDropdownOptions(props.applicationMetadata.unitOfMeasurementInputs, ["id"], "name"),
            defaultUnitValue: GlobalConstants.UnitMeasurements.ACRE
          },
          {
            inputSuffix: true,
            value: props.borrowerApplicationInfo.sizeOfProposedLot,
            label: "Size of proposed lots:",
            size: 4,
            inputType: GlobalConstants.InputTypes.INT,
            fieldType: "inputSuffix",
            field: "sizeOfProposedLot",
            unitField: "sizeOfProposedLotUnitOfMeasurementBorrowerApplicationInputId",
            dropdownOptions: getDropdownOptions(props.applicationMetadata.unitOfMeasurementInputs, ["id"], "name"),
            defaultUnitValue: GlobalConstants.UnitMeasurements.SQFT
          }] : []),
          {
            value: props.projectInfo.cashDown,
            label: "Cash Down:",
            size: 4,
            unitType: "$",
            field: "cashDown",
            original: props.borrowerApplicationInfo.cashDown,
            readonly: true
          },
        ]}
      />
      {props.projectInfo.builder && <BPLoanDetailSection
        title={BrokerConstants.InteriorCardAccordions.BuilderInfo.name}
        accordionId={BrokerConstants.InteriorCardAccordions.BuilderInfo.id}
        mainPatchObj={props.projectPatchObj}
        setMainPatchObj={props.setProjectPatchObj}
        subPatchObjName="builder"
        subPatchObj={builderInfoPatchObj}
        setSubPatchObj={() => handleBuilderInfoChange()}
        isEditMode={props.isEditMode}
        {...props.accordionProps}
        specialState="The Builder is the Borrowing Entity"
        customStateActionButton="Edit Borrowing Entity"
        customStateAction={() => editBuilderInBorrwerTab()}
        inputs={builderIsBorrwer ? null : [
          {
            value: props.projectInfo.builder && props.projectInfo.builder.name,
            label: "Builder Organization:",
            size: 4,
            field: "name"
          },
          {
            value: props.projectInfo.builder && props.projectInfo.builder.entitySubTypeId,
            label: "Type:",
            size: 4,
            field: "entitySubTypeId",
            dropdown: getDropdownOptions(props.applicationMetadata.entitySubTypes, ["id"], "name")
          },
          {
            value: deepCopy(props.projectInfo.builder), 
            original: props.borrowerApplicationInfo.builder,
            address: true,
            defaultValueObj: deepCopy(props.projectInfo.builder),
            placeDetailsObject: builderInfoPatchObj,
            setPlaceDetailsObject: (placeDetailsObject) => handleBuilderAddressChange(placeDetailsObject, props.projectPatchObj, props.setProjectPatchObj),
            fieldsNameOverridesArray: [
              {
                addressComponent: "address",
                field: "physicalStreet"
              },
              {
                addressComponent: "city",
                field: "physicalCityName"
              },
              {
                addressComponent: "state",
                field: "physicalStateName"
              },
              {
                addressComponent: "county",
                field: "physicalCountyName"
              },
              {
                addressComponent: "zip",
                field: "physicalZipCode"
              },
            ],
            label: "Address",
            size: 8
          }
        ]}
      />}
      {((!FeatureFlags.IS_UNIT_PROPERTY_REDESIGN)
        || (FeatureFlags.IS_UNIT_PROPERTY_REDESIGN && (props.projectInfo.loanProgramId !== GlobalConstants.LoanProgramIds.BRIDGE && props.projectInfo.loanProgramId !== GlobalConstants.LoanProgramIds.DEVELOPMENT)))
        && <BPLoanDetailSection
        title={BrokerConstants.InteriorCardAccordions.DetailsTerm.name}
        accordionId={BrokerConstants.InteriorCardAccordions.DetailsTerm.id}
        mainPatchObj={props.projectPatchObj}
        setMainPatchObj={props.setProjectPatchObj}
        subPatchObjName="termSchedule" 
        subPatchObj={props.projectPatchObj}
        setSubPatchObj={props.setProjectPatchObj}
        isEditMode={props.isEditMode && props.isLoanInfo}
        {...props.accordionProps}
        table={props.propertiesCopy && <Grid>
          {FeatureFlags.IS_UNIT_PROPERTY_REDESIGN ? <Grid.Row className="details-row">
            <Grid.Column width={16}>
              <BPConstructionScheduleTable 
                isLoan={true}
                propertiesPatchArray={props.propertiesPatchArray}
                properties={props.propertiesCopy}
                checkedPropertySchedules={checkedPropertySchedules}
                setCheckedPropertySchedules={setCheckedPropertySchedules}
                projectInfo={{
                  "loanProgramId" : props.projectInfo.loanProgramId, 
                  "developmentBeginsAfterClosingInMonths" : props.projectInfo.developmentBeginsAfterClosingInMonths, 
                  "timeFrameToDevelopInMonths" : props.projectInfo.timeFrameToDevelopInMonths,
                  "requestedClosingDate" : props.projectInfo.requestedClosingDate,
                }}
                handlePropertyLoanTermChange={handlePropertyLoanTermChange}
                brokerColor={props.brokerColor}
                disabled={!(props.isEditMode && props.isLoanInfo)}
                invalidProps={props.invalidProps}
              />
            </Grid.Column>
          </Grid.Row>
          : <Grid.Row className="details-row">
            <Grid.Column width={16}>
              <BCFormInput
                labelClass="gmr-background-label"
                label="Term Schedule"
              />
            </Grid.Column>
            <Grid.Column width={16}>
              <BCTable
                editMode={props.isEditMode && props.isLoanInfo}
                data={formatLoanUnitsTermSchedule(props.propertiesCopy)}
                thin
                dataSingleLine
                overflowX
                unstackable
                brokerColor={getIsWhiteLabeled() ? props.brokerColor : void 0}
              />
            </Grid.Column>
          </Grid.Row>}
        </Grid>}
      />}
    </React.Fragment>
  );
};
  
  export default BPLoanProjectContent;